import { useEffect, useState } from "react"
import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from "@mui/material"
import CardIcon from "@mui/icons-material/CreditCardOutlined"
import NoCardIcon from "@mui/icons-material/CreditCardOffOutlined"

import { billingApi } from "../../api"
import { formatDate } from "../../utils/format"

/**
 * Whether this client can be charged, and with what.
 *
 * ── Why it belongs next to the client picker ────────────────────────────────
 *
 * "Charge automatically" is refused by the server for a client with no card, and
 * that refusal is correct but late: by then somebody has drafted an invoice and
 * pressed send, and what they learn is that the last two minutes were wasted.
 * The same fact stated beside the name they just chose costs nothing and is
 * read before anything is built.
 *
 * ── Why a card on file is not the same as a card that works ─────────────────
 *
 * This says a card is authorised, which means Stripe told us the client
 * completed the form. It does not promise the next charge succeeds — a card
 * expires, a bank declines, a limit is hit. That is what the declined state on
 * the invoice list is for, and why this deliberately does not say "will be
 * paid".
 */
export default function ClientPaymentMethod({ clientId, sx }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!clientId) {
      setState(null)
      return
    }
    setLoading(true)
    billingApi
      .autopay({ client_id: clientId })
      .then(setState)
      .catch(() => setState(null))
      .finally(() => setLoading(false))
  }, [clientId])

  if (!clientId) return <Box sx={sx} />
  if (loading) return <Skeleton variant="rounded" height={32} sx={{ maxWidth: 260, ...sx }} />
  if (!state) return <Box sx={sx} />

  const card = state.card

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={sx}>
      {card && state.authorized ? (
        <Tooltip
          title={
            state.authorized_at
              ? `Authorised ${formatDate(state.authorized_at)}. A card on file is permission to charge, not a promise the charge succeeds.`
              : "Authorised"
          }
        >
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<CardIcon sx={{ fontSize: 14 }} />}
            label={`${card.brand} ending ${card.last4}`}
            sx={{ height: 24, textTransform: "capitalize" }}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Nothing can be collected automatically from this client until they authorise a card. They do that from their own Invoices screen.">
          <Chip
            size="small"
            variant="outlined"
            icon={<NoCardIcon sx={{ fontSize: 14 }} />}
            label="No card on file"
            sx={{ height: 24 }}
          />
        </Tooltip>
      )}
      {!state.configured ? (
        <Typography variant="caption" color="text.secondary">
          Stripe is not configured here
        </Typography>
      ) : null}
    </Stack>
  )
}
